import { jsx as _jsx } from "react/jsx-runtime";
import { addSuccessNotification, getCompletedOrderById, isEmptyArray, loadCompletedOrderById, loadSeatAvailabilityForShowtime, loadSeatLayoutForShowtime, loadShowtimeById, logger, mapSeatToOrderSeat, NotificationType, swapSeatsForCompletedOrder } from '@vista/omnichannel-components-domain';
import { bemClassBuilder } from '@vista-digital/bem-class-builder';
import { OrderType } from '@vista-digital/ocapi-types/v1';
import { observer } from 'mobx-react';
import { forwardRef, useCallback, useEffect, useImperativeHandle, useMemo, useRef, useState } from 'react';
import { useOccConfig } from '../../context/occ/hooks/use-occ-config';
import { getIdForComponentNotification } from '../../utils/get-id-for-component-notification';
import { getSwapSeatAvailabilityStatus, SwapSeatUnavailableReason } from '../../utils/swap-seats-utils';
import { withComponentWrapper } from '../../utils/with-component-wrapper';
import { SeatPicker } from '../seat-picker/seat-picker';
import { SeatPickerSkeleton } from '../seat-picker/subcomponents/skeleton';
import { removeProceedErrorNotification } from '../showtime/order-seat-picker/utils/notification';
import { SwapSeatPickerMessage } from './subcomponents/message';
import { defaultSwapSeatPickerProps, swapSeatPickerPropsValidator } from './types/props';
import { addProceedErrorNotification } from './utils/notification';
import { getSeatingDataForShowtime } from './utils/seating-data-utils';
const b = bemClassBuilder('v-swap-seat-picker');
const SwapSeatPickerBase = (props, ref) => {
    const { enableSkeletonLoadingIndicators } = useOccConfig();
    const { orderId, showtimeId, friendSeatIds, events, displayText, assets, config } = props;
    const { onSelectedSeatsChanged } = events;
    const inputRef = useRef(null);
    const [selectedSeats, setSelectedSeats] = useState([]);
    const [errorUnavailableReason, setErrorUnavailableReason] = useState();
    // Load the order so we have the availability status.
    useEffect(() => {
        (async () => {
            try {
                await loadCompletedOrderById(orderId);
            }
            catch (error) {
                logger.error(error);
                setErrorUnavailableReason(SwapSeatUnavailableReason.FailedToLoadData);
            }
        })();
    }, [orderId]);
    const order = getCompletedOrderById(orderId);
    const orderShowtime = order && getOrderShowtime(order, showtimeId);
    // After the order has been loaded, check the availability status and load the other data.
    useEffect(() => {
        (async () => {
            if (!orderShowtime)
                return;
            const availabilityStatus = getSwapSeatAvailabilityStatus(orderShowtime);
            if (!availabilityStatus.isAvailable) {
                setErrorUnavailableReason(availabilityStatus.unavailableReason);
                return;
            }
            try {
                await Promise.all([
                    loadShowtimeById(showtimeId),
                    loadSeatLayoutForShowtime(showtimeId),
                    loadSeatAvailabilityForShowtime(showtimeId, orderId)
                ]);
            }
            catch (error) {
                logger.error(error);
                setErrorUnavailableReason(SwapSeatUnavailableReason.FailedToLoadData);
            }
        })();
    }, [orderId, showtimeId, orderShowtime]);
    const reservedSeats = orderShowtime === null || orderShowtime === void 0 ? void 0 : orderShowtime.seats;
    useEffect(() => {
        if (!reservedSeats)
            return; // order still loading
        if (isEmptyArray(reservedSeats))
            throw Error('No seats are reserved for this order. Cannot swap seats.');
        setSelectedSeats(reservedSeats);
    }, [reservedSeats]);
    useImperativeHandle(ref, () => ({
        validateSeatSelections: () => inputRef.current.validateSeatSelections(),
        handleProceed
    }));
    const handleProceed = useCallback(async () => {
        try {
            if (haveSameSeatsBeenSelected(selectedSeats, reservedSeats))
                return true;
            removeProceedErrorNotification();
            const validationResult = inputRef.current.validateSeatSelections();
            if (validationResult.isValid) {
                await swapSeatsForCompletedOrder(orderId, showtimeId, selectedSeats.map(seat => seat.seatId));
                addSuccessNotification({
                    id: getIdForComponentNotification('swap-seat-picker', NotificationType.Success, 'swap-seats'),
                    title: displayText.swapSeatsSuccessfulTitle,
                    messages: [displayText.swapSeatsSuccessfulMessage]
                });
                return true;
            }
        }
        catch (error) {
            addProceedErrorNotification(displayText, error);
        }
        return false; // selection invalid or API error
    }, [displayText, orderId, reservedSeats, selectedSeats, showtimeId]);
    const seatingData = getSeatingDataForShowtime(showtimeId, friendSeatIds, reservedSeats);
    const handleSelectedSeatsChanged = useCallback((seats) => {
        if (!seatingData)
            throw new Error('Cannot handle selected seats changed without seating data');
        setSelectedSeats(seats.map(seat => mapSeatToOrderSeat(seat, seatingData.seatLayout.areas)));
        onSelectedSeatsChanged === null || onSelectedSeatsChanged === void 0 ? void 0 : onSelectedSeatsChanged(seats);
    }, [onSelectedSeatsChanged, seatingData]);
    const mappedEvents = useMemo(() => (Object.assign(Object.assign({}, props.events), { onSelectedSeatsChanged: handleSelectedSeatsChanged })), [props.events, handleSelectedSeatsChanged]);
    if (errorUnavailableReason) {
        return (_jsx(SwapSeatPickerMessage, { unavailableReason: errorUnavailableReason, displayText: displayText, assets: assets }));
    }
    if (!seatingData || !orderShowtime) {
        return enableSkeletonLoadingIndicators ? _jsx(SeatPickerSkeleton, {}) : null;
    }
    const availabilityStatus = getSwapSeatAvailabilityStatus(orderShowtime);
    if (!availabilityStatus.isAvailable) {
        setErrorUnavailableReason(availabilityStatus.unavailableReason);
    }
    const selectedSeatIds = selectedSeats.map(seat => seat.seatId);
    return (_jsx("div", Object.assign({ className: b.block() }, { children: _jsx(SeatPicker, { ref: inputRef, data: seatingData, selectedSeatIds: selectedSeatIds, events: mappedEvents, displayText: displayText, assets: assets, config: Object.assign(Object.assign({}, config), { 
                // Enforce mustRespectSeatSelectionLimits
                selectionRules: Object.assign(Object.assign({}, config.selectionRules), { mustRespectSeatSelectionLimits: { enabled: true } }) }) }) })));
};
export const SwapSeatPicker = observer(forwardRef(SwapSeatPickerBase));
SwapSeatPicker.displayName = 'SwapSeatPicker';
/**
 * The **Swap Seat Picker** component renders a **Seat Picker** component for the next showtime in an order with its
 * selected seats. Patrons can use this component to swap the seats in their order.
 */
export const SwapSeatPickerComponent = withComponentWrapper(SwapSeatPicker, defaultSwapSeatPickerProps, swapSeatPickerPropsValidator);
const isCompletedStandardOrder = (order) => {
    return order.type === OrderType.Standard;
};
const getOrderShowtime = (order, showtimeId) => {
    if (!isCompletedStandardOrder(order))
        throw new Error(`Invalid completed order ${order.id}`);
    const orderShowtime = order.showtimes.find(s => s.showtimeId === showtimeId);
    if (!orderShowtime)
        throw new Error('Unable to find order showtime for journey.');
    return orderShowtime;
};
const haveSameSeatsBeenSelected = (selectedSeats, reservedSeats) => {
    if (!reservedSeats || selectedSeats.length !== reservedSeats.length)
        return false;
    const sortedSavedSeatIds = [...selectedSeats].map(seat => seat.seatId).sort();
    const sortedReservedSeatIds = [...reservedSeats].map(seat => seat.seatId).sort();
    return sortedSavedSeatIds.every((seatId, i) => seatId === sortedReservedSeatIds[i]);
};
